import { DataGrid, GridColumnVisibilityModel, GridFilterModel, GridPaginationModel, GridSortModel, DataGridProps as MUIDataGridProps } from "@mui/x-data-grid"
import React, { MutableRefObject, useCallback, useRef, useState } from "react"
import { UseInfiniteQueryResult, useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"
import { Box, BoxProps, CircularProgress, SxProps } from "@mui/material"
import { GridApiCommunity } from "@mui/x-data-grid/internals"

import { calculateToFromFilter } from "./InfiniteTableExt"
import { ModuleName } from "@/utils/store/AppStore.types"
import { filterInt, sortInt } from "@/utils/types/InfiniteTable"
import AppStore from "@/utils/store/AppStore"
import { CustomColors } from "@/utils/theme/theme"

var customParseFormat = require("dayjs/plugin/customParseFormat")
var utc = require("dayjs/plugin/utc")
dayjs.extend(customParseFormat)
dayjs.extend(utc)

interface DataGridProps {
  reactQuery: UseInfiniteQueryResult
  rowKey?: string
  infiniteTableKey: ModuleName
  refs: {
    filterRef: MutableRefObject<filterInt | {}>
    sortRef: MutableRefObject<sortInt | {}>
    isNextPage: MutableRefObject<boolean>
    tableAPIRef: MutableRefObject<GridApiCommunity>
  }
  mapper: {
    filterIdMap?: filterInt // @deprecated in support of Advanced Filter "filterKey" key in "columns" prop
    sortIdMap: filterInt // @to_be_deprecated in support of "sortKey" key in "columns" prop
  }
  rows: any
  columns: any
  onRowClick?: (params: any) => void
  getRowClassName?: MUIDataGridProps["getRowClassName"]
  getCellClassName?: MUIDataGridProps["getCellClassName"]
  slots?: {
    tableContainer?: BoxProps
  }
  totalCount?: number
  hiddenColumns?: GridColumnVisibilityModel
}

const InfiniteTable = ({
  rows,
  columns,
  onRowClick,
  mapper,
  refs,
  reactQuery,
  rowKey,
  infiniteTableKey,
  slots,
  totalCount,
  hiddenColumns,
  getRowClassName,
  getCellClassName,
}: DataGridProps) => {
  const gridRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const { filterIdMap = {}, sortIdMap = {} } = mapper
  const { filterRef, sortRef, isNextPage, tableAPIRef } = refs
  
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });


  const { fetchNextPage, isFetching, isFetchingNextPage } = reactQuery
  const MIN_HEIGHT = "392px"

  const CustomNoRowsOverlay = () => {
    return <>{isFetching ? <CircularProgress /> : rows.length === 0 ? <>{"No Records"}</> : ""}</>
  }


  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    // Filter will only provide one filter data as of Feb 14th 2024
    if (filterModel.items.length > 0) {
      const curr = filterModel.items[0]
      // console.info("Filter API :", filterModel);
      const key = filterIdMap[curr.field]
      const pair: filterInt = {
        [key]: curr.value,
      }

      // Only Clears when Value is present // If not then it means the modal is open for first time
      // This DataGrid is very Buggy // Hence the 3rd Condition

      if (curr.field && (curr.value || typeof curr.value === "boolean")) {
        const isDateValid = dayjs(pair[key], "YYYY-MM-DD", true).isValid()
        // console.info("Date Detection :", isDateValid)
        if (isDateValid) {
          pair[key] = dayjs.utc(pair[key]).unix() * 1000
        }
        const rangeFilter = calculateToFromFilter(AppStore.counterCardFilter[infiniteTableKey])
        filterRef.current = {
          ...pair,
          ...rangeFilter,
        }
      } else if (curr.value === "" || (curr.hasOwnProperty("value") && curr.value === undefined)) {
        // Try to reset via reset API if any ?
        filterRef.current = {}
      }

      if (curr.value || typeof curr.value === "boolean" || curr.value === "" || (curr.hasOwnProperty("value") && curr.value === undefined)) {
        queryClient.setQueryData([infiniteTableKey], (data: any) => ({
          pages: [],
          pageParams: 1,
        }))
        setPaginationModel(prev => {
          return { ...prev, page: 0 }
        })
        isNextPage.current = true
        // console.info("Filter Pair :", pair, filterRef.current);
        if (!isFetchingNextPage) {
          fetchNextPage()
        }
      }

      // console.info("Filter API :", pair);
    }
  }, [])

  const onSortChange = (sortModel: GridSortModel) => {
    queryClient.setQueryData([infiniteTableKey], (data: any) => ({
      pages: [],
      pageParams: 1,
    }))
    setPaginationModel(prev => {
      return { ...prev, page: 0 }
    })
    isNextPage.current = true

    if (sortModel.length > 0) {
      const curr = sortModel?.[0]
      const pair: sortInt = {
        field_name: sortIdMap[curr.field] as string,
        orderBy: curr.sort === "asc" ? "asc" : "desc",
      }
      sortRef.current = pair
      // console.info("Sort API :", pair);
    } else {
      sortRef.current = {}
    }
    if (!isFetchingNextPage) {
      fetchNextPage()
    }
  }

  const handlePaginationChange = useCallback((pagination: GridPaginationModel) => {
    if (!isFetchingNextPage) {
      fetchNextPage()
      setPaginationModel(pagination)
    }
  }, [fetchNextPage, isFetchingNextPage])

  return (
    <Box
      className="InfiTable"
      {...slots?.tableContainer}
      sx={[{ height: "max(calc(100% - 80px), 400px)" }, { ...slots?.tableContainer?.sx }] as SxProps}
    >
      <DataGrid
        ref={gridRef}
        apiRef={tableAPIRef}
        rows={rows}
        getRowId={(row) => row[rowKey ?? "id"]} // getRowId(`${row.id}_${pageIndex.current}`)
        // getRowId={(row) => getRowId(`${row[rowKey ?? "id"]}_${pageIndex.current}`)}
        columns={columns}
        pagination
        paginationMode='server'
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={[]}
        rowCount={totalCount || 0}
        sx={{
          "&": {
            borderWidth: 0,
          },
          "& .MuiDataGrid-overlayWrapperInner": {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          },
          "& .MuiDataGrid-virtualScroller": {
            height: "100%",
            minHeight: MIN_HEIGHT,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: CustomColors.adminAppBgColorV2,
            // backgroundColor: "#3E433B",
            color: "#fff",
            // borderRadius: 0,
            // borderWidth: 0,
          },
          ".MuiDataGrid-sortIcon": {
            color: "white",
          },
          ".MuiSvgIcon-root": {
            color: "white",
          },
          "& MuiPopper-root, .MuiDataGrid-menuList": {
            color: CustomColors.adminAppTextPrimary,
            backgroundColor: "red",
          },
          ".MuiDataGrid-withBorderColor": {
            borderColor: CustomColors.adminAppOutlineColor,
          },
          "& .MuiMenuItem-root:hover": {
            backgroundColor: "#272D24",
            filter: " invert(20%)",
          },
          ".MuiDataGrid-columnSeparator .MuiSvgIcon-root": {
            color: CustomColors.adminAppOutlineColor,
          },
          // "& .MuiDataGrid-columnHeaderTitle": {
          //   fontFamily: "Inter",
          //   fontSize: "14px",
          // },
          // "& .MuiDataGrid-cell": {
          //   // fontFamily: "Inter",
          //   // fontSize: "14px",
          //   borderBottom: "1px solid rgba(58, 207, 1, 0.40)",
          // },
          // "& .MuiDataGrid-row:first-of-type": {
          //   borderTopWidth: 0,
          // },
          // "& .MuiDataGrid-row": {
          //   background: "#272D24",
          //   ":hover": {
          //     background: "rgba(27, 146, 10, 0.20)",
          //   },
          // },
          borderColor: "transparent",
          width: "auto",
          color: CustomColors.adminAppTextSecondary,
        }}
        onRowClick={onRowClick}
        filterMode="server"
        sortingMode="server"
        onFilterModelChange={onFilterChange}
        onSortModelChange={onSortChange}
        filterDebounceMs={1000}
        columnVisibilityModel={{ ...hiddenColumns }}
        loading={isFetching || isFetchingNextPage}
        slotProps={{
          filterPanel: {
            disableAddFilterButton: false,
            sx: {
              "& .MuiDataGrid-filterFormOperatorInput": {
                display: "none",
              },
            },
          },
        }}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        getRowClassName={getRowClassName}
        getCellClassName={getCellClassName}
      />
    </Box>
  )
}

export default InfiniteTable
